import React, { useState, useEffect, useRef } from 'react';
import AddLinkModal from '../components/AddLinkModal';
import Toast from '../../../atoms/Toast';
import { API_ENDPOINTS } from '../../../../config';
import { api } from '../../../../utils/api';
import '../../../../styles/profile/links-tab.css';

interface UserLink {
  uid: string;
  type: string;
  handle: string | null;
  custom_handle: {
    title: string;
    url: string;
    description?: string;
  } | null;
  created_at: string;
  updated_at: string;
}

interface UserData {
  urls: UserLink[] | null;
}

interface LinksTabProps {
  userData: UserData | null;
  urls: UserLink[];
}

interface CustomLink {
  uid: string;
  title: string;
  url: string;
  description?: string;
}

interface CustomLinkCardProps {
  link: CustomLink;
  onEdit: (link: CustomLink) => void;
  onDelete: (link: CustomLink) => void;
}

function CustomLinkCard({ link, onEdit, onDelete }: CustomLinkCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="links-tab-custom-card">
      <div className="links-tab-custom-card-header">
        <div className="links-tab-custom-card-icon">🔗</div>
        <div className="links-tab-custom-card-info">
          <h3 className="links-tab-custom-card-title">{link.title}</h3>
          <a 
            href={link.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="links-tab-custom-card-url"
          >
            {link.url}
          </a>
        </div>
        <div className="links-tab-custom-card-menu-container" ref={menuRef}>
          <button
            type="button"
            className="links-tab-custom-card-menu"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="links-tab-custom-card-dropdown">
              <button
                className="links-tab-custom-card-dropdown-item"
                onClick={() => {
                  onEdit(link);
                  setMenuOpen(false);
                }}
              >
                Edit link
              </button>
              <button
                className="links-tab-custom-card-dropdown-item links-tab-custom-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(link);
                  setMenuOpen(false);
                }}
              >
                Delete link
              </button>
            </div>
          )}
        </div>
      </div>
      {link.description && (
        <div className="links-tab-custom-card-body">
          <p className="links-tab-custom-card-description">{link.description}</p>
        </div>
      )}
    </div>
  );
}

export default function LinksTab({ userData, urls }: LinksTabProps) {
  const [linkedIn, setLinkedIn] = useState('');
  const [github, setGithub] = useState('');
  const [twitter, setTwitter] = useState('');
  const [dribbble, setDribbble] = useState('');
  const [editingField, setEditingField] = useState<string | null>(null);
  const [otherLinks, setOtherLinks] = useState<CustomLink[]>([]);
  const [addLinkModalOpen, setAddLinkModalOpen] = useState(false);
  const [editLinkModalOpen, setEditLinkModalOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [selectedLink, setSelectedLink] = useState<CustomLink | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<CustomLink | null>(null);

  const [linkedInUid, setLinkedInUid] = useState<string | null>(null);
  const [githubUid, setGithubUid] = useState<string | null>(null);
  const [twitterUid, setTwitterUid] = useState<string | null>(null);
  const [dribbbleUid, setDribbbleUid] = useState<string | null>(null);

  const [initialValues, setInitialValues] = useState({
    linkedIn: '',
    github: '',
    twitter: '',
    dribbble: '',
    otherLinks: [] as CustomLink[],
  });

  useEffect(() => {
    if (urls && urls.length > 0) {
      const customLinksData: CustomLink[] = [];

      urls.forEach((link) => {
        if (link.type === 'linkedin') {
          setLinkedIn(link.handle || '');
          setLinkedInUid(link.uid);
        } else if (link.type === 'github') {
          setGithub(link.handle || '');
          setGithubUid(link.uid);
        } else if (link.type === 'twitter') {
          setTwitter(link.handle || '');
          setTwitterUid(link.uid);
        } else if (link.type === 'dribbble') {
          setDribbble(link.handle || '');
          setDribbbleUid(link.uid);
        } else if (link.custom_handle) {
          customLinksData.push({
            uid: link.uid,
            title: link.custom_handle.title || '',
            url: link.custom_handle.url || '',
            description: link.custom_handle.description || '',
          });
        }
      });

      setOtherLinks(customLinksData);

      setInitialValues({
        linkedIn: urls.find(l => l.type === 'linkedin')?.handle || '',
        github: urls.find(l => l.type === 'github')?.handle || '',
        twitter: urls.find(l => l.type === 'twitter')?.handle || '',
        dribbble: urls.find(l => l.type === 'dribbble')?.handle || '',
        otherLinks: customLinksData,
      });
    }
  }, [urls]);


  const handleSaveSocialLink = async (type: 'linkedin' | 'github' | 'twitter' | 'dribbble', handle: string, uid: string | null) => {
    setSavingField(type);

    try {
      if (!handle.trim() && uid) {
        
        await api.delete(API_ENDPOINTS.users.deleteUrl(uid));
        

        if (type === 'linkedin') setLinkedInUid(null);
        else if (type === 'github') setGithubUid(null);
        else if (type === 'twitter') setTwitterUid(null);
        else if (type === 'dribbble') setDribbbleUid(null);

        setInitialValues(prev => ({
          ...prev,
          [type === 'linkedin' ? 'linkedIn' : type === 'github' ? 'github' : type === 'twitter' ? 'twitter' : 'dribbble']: '',
        }));

        setToast({ message: `${type.charAt(0).toUpperCase() + type.slice(1)} link deleted! ✓`, type: 'success' });
        return;
      }

      if (!handle.trim()) {
        return;
      }

      const payload: any = {
        type: type,
        handle: handle.trim(),
      };

      if (uid) {
        payload.uid = uid;
      }


      const response = await api.post(API_ENDPOINTS.users.updateUrl, payload);
      

      if (response && response.uid && !uid) {
        if (type === 'linkedin') setLinkedInUid(response.uid);
        else if (type === 'github') setGithubUid(response.uid);
        else if (type === 'twitter') setTwitterUid(response.uid);
        else if (type === 'dribbble') setDribbbleUid(response.uid);
      }

      setInitialValues(prev => ({
        ...prev,
        [type === 'linkedin' ? 'linkedIn' : type === 'github' ? 'github' : type === 'twitter' ? 'twitter' : 'dribbble']: handle.trim(),
      }));

      setToast({ message: `${type.charAt(0).toUpperCase() + type.slice(1)} link saved! ✓`, type: 'success' });
    } catch (error) {
      setToast({ message: `Failed to save ${type} link. Please try again.`, type: 'error' });
    } finally {
      setSavingField(null);
    }
  };

  const handleImport = () => {
  };


  const handleAddOtherLink = () => {
    setSelectedLink(null);
    setAddLinkModalOpen(true);
  };

  const handleEditLink = (link: CustomLink) => {
    setSelectedLink(link);
    setEditLinkModalOpen(true);
  };

  const handleDeleteLinkClick = (link: CustomLink) => {
    setLinkToDelete(link);
    setDeleteConfirmOpen(true);
  };

  const handleAddLink = async (data: { title: string; url: string; description: string }) => {
    try {
      const payload: any = {
        type: 'other',
        custom_handle: {
          title: data.title.trim(),
          url: data.url.trim(),
        },
      };

      if (data.description?.trim()) {
        payload.custom_handle.description = data.description.trim();
      }


      const response = await api.post(API_ENDPOINTS.users.updateUrl, payload);
      

      if (response && response.uid) {
        setOtherLinks([...otherLinks, {
          uid: response.uid,
          title: data.title.trim(),
          url: data.url.trim(),
          description: data.description?.trim() || '',
        }]);
      }

      setAddLinkModalOpen(false);
      setToast({ message: 'Link added successfully! ✓', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to add link. Please try again.', type: 'error' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!linkToDelete) return;

    if (linkToDelete.uid) {
      try {
        await api.delete(API_ENDPOINTS.users.deleteUrl(linkToDelete.uid));
        setToast({ message: 'Link deleted successfully! ✓', type: 'success' });
      } catch (error) {
        setToast({ message: 'Failed to delete link. Please try again.', type: 'error' });
        setDeleteConfirmOpen(false);
        setLinkToDelete(null);
        return;
      }
    }

    setOtherLinks(otherLinks.filter(l => l.uid !== linkToDelete.uid));
    setDeleteConfirmOpen(false);
    setLinkToDelete(null);
  };

  const handleUpdateLink = async (data: { title: string; url: string; description: string }) => {
    if (!selectedLink || !selectedLink.uid) return;

    try {
      const payload: any = {
        uid: selectedLink.uid,
        type: 'other',
        custom_handle: {
          title: data.title.trim(),
          url: data.url.trim(),
        },
      };

      if (data.description?.trim()) {
        payload.custom_handle.description = data.description.trim();
      }


      const response = await api.post(API_ENDPOINTS.users.updateUrl, payload);
      

      setOtherLinks(otherLinks.map(link => 
        link.uid === selectedLink.uid ? {
          uid: selectedLink.uid,
          title: data.title.trim(),
          url: data.url.trim(),
          description: data.description?.trim() || '',
        } : link
      ));

      setEditLinkModalOpen(false);
      setSelectedLink(null);
      setToast({ message: 'Link updated successfully! ✓', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to update link. Please try again.', type: 'error' });
    }
  };


  return (
    <>
      <div className="links-tab">
        <div className="links-tab-header">
          <h1 className="links-tab-title">Links</h1>
          <button className="links-tab-btn links-tab-btn--import" onClick={handleImport} type="button">
            <span className="links-tab-btn-icon">⬇</span>
            Import
          </button>
        </div>

        <div className="links-tab-content">
          <div className="links-tab-section">
            <h2 className="links-tab-section-title">Social Media Handles</h2>
            
            <div className="links-tab-social-items">
              <div className="links-tab-social-item" onClick={() => setEditingField('linkedin')}>
                <div className="links-tab-social-icon links-tab-social-icon--linkedin">in</div>
                <div className="links-tab-social-content">
                  <div className="links-tab-social-name">LinkedIn</div>
                  {editingField === 'linkedin' ? (
                    <input
                      type="text"
                      className="links-tab-social-input"
                      placeholder="username"
                      value={linkedIn}
                      onChange={(e) => setLinkedIn(e.target.value)}
                      onBlur={() => {
                        setEditingField(null);
                        if (linkedIn !== initialValues.linkedIn) {
                          handleSaveSocialLink('linkedin', linkedIn, linkedInUid);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <div className="links-tab-social-url">
                      <span className="links-tab-social-url-base">linkedin.com/in/</span>
                      <span className="links-tab-social-url-handle">
                        {savingField === 'linkedin' ? 'Saving...' : linkedIn || 'username'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="links-tab-social-item" onClick={() => setEditingField('github')}>
                <div className="links-tab-social-icon links-tab-social-icon--github">
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
                  </svg>
                </div>
                <div className="links-tab-social-content">
                  <div className="links-tab-social-name">GitHub</div>
                  {editingField === 'github' ? (
                    <input
                      type="text"
                      className="links-tab-social-input"
                      placeholder="username"
                      value={github}
                      onChange={(e) => setGithub(e.target.value)}
                      onBlur={() => {
                        setEditingField(null);
                        if (github !== initialValues.github) {
                          handleSaveSocialLink('github', github, githubUid);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <div className="links-tab-social-url">
                      <span className="links-tab-social-url-base">github.com/</span>
                      <span className="links-tab-social-url-handle">
                        {savingField === 'github' ? 'Saving...' : github || 'enter-handle'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="links-tab-social-item" onClick={() => setEditingField('twitter')}>
                <div className="links-tab-social-icon links-tab-social-icon--twitter">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <div className="links-tab-social-content">
                  <div className="links-tab-social-name">Twitter</div>
                  {editingField === 'twitter' ? (
                    <input
                      type="text"
                      className="links-tab-social-input"
                      placeholder="username"
                      value={twitter}
                      onChange={(e) => setTwitter(e.target.value)}
                      onBlur={() => {
                        setEditingField(null);
                        if (twitter !== initialValues.twitter) {
                          handleSaveSocialLink('twitter', twitter, twitterUid);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <div className="links-tab-social-url">
                      <span className="links-tab-social-url-base">twitter.com/</span>
                      <span className="links-tab-social-url-handle">
                        {savingField === 'twitter' ? 'Saving...' : twitter || 'enter-handle'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="links-tab-social-item" onClick={() => setEditingField('dribbble')}>
                <div className="links-tab-social-icon links-tab-social-icon--dribbble">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.375 0 0 5.375 0 12s5.375 12 12 12 12-5.375 12-12S18.625 0 12 0zm8.925 6.5c1.075 1.475 1.75 3.3 1.8 5.25-3.25-.675-6.15-.575-8.5.05-.3-.725-.6-1.45-.925-2.15 2.7-1.125 4.775-2.525 5.625-3.15zM12 2.25c2.125 0 4.075.7 5.65 1.875-.675.575-2.6 1.875-5.15 2.875-1.525-2.8-3.15-5.05-3.35-5.325A9.713 9.713 0 0112 2.25zM6.975 2.625c.2.25 1.8 2.5 3.35 5.275-4.225 1.125-7.95 1.1-8.4 1.1.725-2.875 2.6-5.225 5.05-6.375zM2.225 12v-.325c.475.025 4.775.1 9.35-1.3.35.675.675 1.35.975 2.025-3.275 1.025-6.025 3.85-7.275 5.525A9.656 9.656 0 012.225 12zm9.775 9.75c-1.95 0-3.75-.575-5.275-1.55 1.05-1.525 3.4-4.075 7.025-5.3 1.15 3 1.925 5.525 2.1 6.225A9.643 9.643 0 0112 21.75zm5.825-1.65c-.125-.575-.85-2.975-1.925-5.925 2.175-.35 4.775-.2 7.575.25-.55 2.6-2.025 4.825-4.15 6.2-.375-.2-.5-.375-.5-.525z"/>
                  </svg>
                </div>
                <div className="links-tab-social-content">
                  <div className="links-tab-social-name">Dribbble</div>
                  {editingField === 'dribbble' ? (
                    <input
                      type="text"
                      className="links-tab-social-input"
                      placeholder="username"
                      value={dribbble}
                      onChange={(e) => setDribbble(e.target.value)}
                      onBlur={() => {
                        setEditingField(null);
                        if (dribbble !== initialValues.dribbble) {
                          handleSaveSocialLink('dribbble', dribbble, dribbbleUid);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <div className="links-tab-social-url">
                      <span className="links-tab-social-url-base">dribbble.com/</span>
                      <span className="links-tab-social-url-handle">
                        {savingField === 'dribbble' ? 'Saving...' : dribbble || 'enter-handle'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="links-tab-section">
            <h2 className="links-tab-section-title">Other Links</h2>
            
            <div className="links-tab-other-items">
              {otherLinks.map((link) => (
                <CustomLinkCard
                  key={link.uid}
                  link={link}
                  onEdit={handleEditLink}
                  onDelete={handleDeleteLinkClick}
                />
              ))}
              
              <button
                type="button"
                className="links-tab-add-btn"
                onClick={handleAddOtherLink}
              >
                <span className="links-tab-add-icon">+</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddLinkModal
        open={addLinkModalOpen}
        onClose={() => setAddLinkModalOpen(false)}
        onAdd={handleAddLink}
      />

      <AddLinkModal
        open={editLinkModalOpen}
        onClose={() => {
          setEditLinkModalOpen(false);
          setSelectedLink(null);
        }}
        onAdd={handleUpdateLink}
        title="Edit Link"
        initialData={selectedLink ? {
          title: selectedLink.title,
          url: selectedLink.url,
          description: selectedLink.description || '',
        } : undefined}
        isEditMode={true}
      />

      {deleteConfirmOpen && (
        <div className="links-tab-delete-overlay" onClick={() => setDeleteConfirmOpen(false)}>
          <div className="links-tab-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="links-tab-delete-title">Delete Link</h3>
            <p className="links-tab-delete-message">
              Are you sure you want to delete "{linkToDelete?.title}"?
            </p>
            <div className="links-tab-delete-actions">
              <button
                className="links-tab-delete-btn links-tab-delete-btn--cancel"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setLinkToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="links-tab-delete-btn links-tab-delete-btn--confirm"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}
